
import React from 'react';
import { 
  Flame, 
  Droplets, 
  Eye, 
  ShowerHead, 
  Bell, 
  Lightbulb, 
  ShieldCheck,
  Footprints,
  Users,
  HardHat,
  Leaf,
  Zap,
  Star,
  Activity,
  BriefcaseMedical,
  Container,
  Move,
  RectangleHorizontal,
  Cable,
  Stethoscope,
  Anchor,
  Wind,
  Beaker,
  HeartHandshake
} from 'lucide-react';
import { 
  EquipmentType, 
  Frequency, 
  Equipment, 
  InspectionType, 
  UserRole, 
  AppUser, 
  InspectionPlan, 
  InspectionActual, 
  InspectionStatus, 
  Finding, 
  FollowUpStatus, 
  FindingLevel, 
  SQCDME, 
  BusinessRisk,
  PlanStatus
} from './types';
import { AreaConfig } from './App';

export const EQUIPMENT_ICONS: Record<EquipmentType, React.ReactNode> = {
  [EquipmentType.APAR]: <Flame className="w-5 h-5 text-red-500" />,
  [EquipmentType.HYDRANT]: <Droplets className="w-5 h-5 text-blue-500" />,
  [EquipmentType.EYEWASH]: <Eye className="w-5 h-5 text-emerald-500" />,
  [EquipmentType.SHOWER]: <ShowerHead className="w-5 h-5 text-cyan-500" />,
  [EquipmentType.DETECTOR]: <Bell className="w-5 h-5 text-amber-500" />,
  [EquipmentType.LAMP]: <Lightbulb className="w-5 h-5 text-yellow-500" />,
  [EquipmentType.PPE]: <ShieldCheck className="w-5 h-5 text-indigo-500" />,
  [EquipmentType.FIRST_AID]: <BriefcaseMedical className="w-5 h-5 text-rose-500" />,
  [EquipmentType.SPILL_KIT]: <Container className="w-5 h-5 text-yellow-600" />,
  [EquipmentType.STRETCHER]: <Move className="w-5 h-5 text-orange-500" />,
  [EquipmentType.FIRE_BLANKET]: <RectangleHorizontal className="w-5 h-5 text-red-600" />,
};

export const INSPECTION_TYPE_ICONS: Record<InspectionType, React.ReactNode> = {
  [InspectionType.ROUTINE]: <ShieldCheck className="w-4 h-4" />,
  [InspectionType.GENBA_WALK]: <Footprints className="w-4 h-4" />,
  [InspectionType.GENBA_BOD]: <Users className="w-4 h-4" />,
  [InspectionType.SAFETY_PATROL]: <HardHat className="w-4 h-4" />,
  [InspectionType.ENV_WALK]: <Leaf className="w-4 h-4" />,
  [InspectionType.FIVE_S]: <Star className="w-4 h-4" />,
  [InspectionType.FIRE_SAFETY]: <Flame className="w-4 h-4" />,
  [InspectionType.ENERGY_WALK]: <Zap className="w-4 h-4" />,
  [InspectionType.MANAGEMENT_WALK]: <Activity className="w-4 h-4" />,
  [InspectionType.FIRE_ELECTRICAL]: <Cable className="w-4 h-4" />,
  [InspectionType.WORKPLACE_HEALTH]: <Stethoscope className="w-4 h-4" />,
  [InspectionType.LIFTING_EQUIPMENT]: <Anchor className="w-4 h-4" />,
  [InspectionType.PROD_RISK]: <Wind className="w-4 h-4" />,
  [InspectionType.CHEMICAL_PAINTING]: <Beaker className="w-4 h-4" />,
  [InspectionType.SAFETY_CULTURE]: <HeartHandshake className="w-4 h-4" />,
};

export const MOCK_AREAS: AreaConfig[] = [
  {
    id: 'area-1',
    name: 'Plant 1 - Production',
    sections: [
      { name: 'Assembly Line', defaultPIC: 'Nasukha' },
      { name: 'Machining Center', defaultPIC: 'Agus' },
      { name: 'Utility & Genset', defaultPIC: 'Budi' }
    ]
  },
  {
    id: 'area-2',
    name: 'Plant 2 - Warehouse',
    sections: [
      { name: 'RM Storage', defaultPIC: 'Siti' },
      { name: 'Chemical Room', defaultPIC: 'Hendra' },
      { name: 'Loading Dock', defaultPIC: 'Lani' }
    ]
  },
  {
    id: 'area-3',
    name: 'Gedung Office',
    sections: [
      { name: 'Lantai 1 - Lobby', defaultPIC: 'Admin Safety' },
      { name: 'Server Room', defaultPIC: 'IT Support' }
    ]
  }
];

export const MOCK_EQUIPMENTS: Equipment[] = [
  // APAR - Fire Extinguishers
  { id: 'eq-1', code: 'APAR-P1-001', type: EquipmentType.APAR, location: 'Pintu Masuk Assembly', area: 'Plant 1 - Production', section: 'Assembly Line', frequency: Frequency.MONTHLY, technicalChecklist: ['Tekanan (Zona Hijau)', 'Segel & Pin Pengaman', 'Kondisi Tabung (No Karat)', 'Selang & Nozzle (Tidak Retak)', 'Label Instruksi Jelas', 'Akses Tidak Terhalang'] },
  { id: 'eq-2', code: 'APAR-P2-005', type: EquipmentType.APAR, location: 'Chemical Storage Area', area: 'Plant 2 - Warehouse', section: 'Chemical Room', frequency: Frequency.MONTHLY, technicalChecklist: ['Tekanan (Zona Hijau)', 'Segel & Pin Pengaman', 'Kondisi Tabung', 'Selang & Nozzle', 'Kesesuaian Media (Co2/Powder)'] },
  
  // SENSORS & DETECTORS
  { id: 'det-1', code: 'SMK-OFF-001', type: EquipmentType.DETECTOR, location: 'Server Room IT', area: 'Gedung Office', section: 'Server Room', frequency: Frequency.QUARTERLY, technicalChecklist: ['Indikator Power (Steady On)', 'Respon Terhadap Smoke Simulator', 'Kondisi Fisik Cleanliness', 'Koneksi ke Panel Alarm (MCFA)'] },
  { id: 'det-2', code: 'GAS-P2-001', type: EquipmentType.DETECTOR, location: 'Chemical Room West', area: 'Plant 2 - Warehouse', section: 'Chemical Room', frequency: Frequency.MONTHLY, technicalChecklist: ['Display Reading Normal', 'Status Kalibrasi (Valid)', 'Visual & Audible Alarm Test', 'Sensor Sensitivity Check'] },
  { id: 'det-3', code: 'HEAT-P1-001', type: EquipmentType.DETECTOR, location: 'Area Dekat Oven Machining', area: 'Plant 1 - Production', section: 'Machining Center', frequency: Frequency.QUARTERLY, technicalChecklist: ['Status Indikator Normal', 'Visual Check (Tidak Terhalang)', 'Test Uji Fungsi Panas'] },

  // HYDRANTS
  { id: 'hyd-1', code: 'HYD-EXT-001', type: EquipmentType.HYDRANT, location: 'Halaman Luar Loading Dock', area: 'Plant 2 - Warehouse', section: 'Loading Dock', frequency: Frequency.MONTHLY, technicalChecklist: ['Kondisi Valve (Mudah Diputar)', 'Hose (Tidak Bocor/Rapuh)', 'Nozzle (Adjustable)', 'Kondisi Box (Cat & Engsel)', 'Akses & Marka Jalan'] },
  
  // EMERGENCY INFRASTRUCTURE
  { id: 'lamp-1', code: 'EL-P1-010', type: EquipmentType.LAMP, location: 'Jalur Evakuasi Assembly', area: 'Plant 1 - Production', section: 'Assembly Line', frequency: Frequency.MONTHLY, technicalChecklist: ['Uji Test Button (Lampu Menyala)', 'Kondisi Battery Backup', 'Brightness Level', 'Sudut Pencahayaan Jalur'] },
  
  // EYEWASH & SHOWER (EMERGENCY RESPONSE)
  { id: 'eye-1', code: 'EWS-P2-001', type: EquipmentType.EYEWASH, location: 'Samping Wastafel Kimia', area: 'Plant 2 - Warehouse', section: 'Chemical Room', frequency: Frequency.WEEKLY, technicalChecklist: ['Debit Air (Simetris)', 'Kebersihan Nozzle', 'Dust Cover Otomatis Terbuka', 'Sistem Drainase Lancar'] },
  
  // KITS & RESPONSE TOOLS
  { id: 'spill-1', code: 'SK-P2-001', type: EquipmentType.SPILL_KIT, location: 'Depan Pintu Chemical', area: 'Plant 2 - Warehouse', section: 'Chemical Room', frequency: Frequency.MONTHLY, technicalChecklist: ['Kelengkapan Absorbent Pads', 'Pillow & Socks Lengkap', 'Kantong Limbah B3 Tersedia', 'APD (Goggle/Sarung Tangan) Siap'] },
  { id: 'p3k-1', code: 'FA-OFF-001', type: EquipmentType.FIRST_AID, location: 'Lobby Utama', area: 'Gedung Office', section: 'Lantai 1 - Lobby', frequency: Frequency.MONTHLY, technicalChecklist: ['Cek Checklist Isi Kotak', 'Masa Kadaluwarsa Obat/Kasa', 'Kebersihan Kotak', 'Mudah Dijangkau'] },
];

export const MOCK_USERS: AppUser[] = [
  { id: 'u1', name: 'Rahmat Super', email: 'super@safety.com', role: UserRole.SUPER_ADMIN, avatar: 'https://i.pravatar.cc/150?u=super' },
  { id: 'u2', name: 'Admin Safety', email: 'admin@safety.com', role: UserRole.ADMIN, plant: 'All Sites', avatar: 'https://i.pravatar.cc/150?u=admin' },
  { id: 'u3', name: 'Budi Inspector', email: 'inspector@safety.com', role: UserRole.INSPECTOR, avatar: 'https://i.pravatar.cc/150?u=inspector' },
  { id: 'u4', name: 'Nasukha', email: 'pic@safety.com', role: UserRole.PIC, avatar: 'https://i.pravatar.cc/150?u=pic' },
  { id: 'u5', name: 'Pak Boss BOD', email: 'bod@safety.com', role: UserRole.MANAGEMENT, avatar: 'https://i.pravatar.cc/150?u=bod' },
];

const todayStr = new Date().toISOString().split('T')[0];
const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];

export const MOCK_PLANS: InspectionPlan[] = [
  { id: 'plan-1', equipmentId: 'eq-1', plannedDate: yesterdayStr, pic: 'Nasukha', status: PlanStatus.COMPLETED },
  { id: 'plan-2', equipmentId: 'hyd-1', plannedDate: todayStr, pic: 'Lani', status: PlanStatus.SCHEDULED },
  { id: 'plan-3', equipmentId: 'det-2', plannedDate: tomorrowStr, pic: 'Siti', status: PlanStatus.SCHEDULED },
];

export const MOCK_ACTUALS: InspectionActual[] = [
  { 
    id: 'act-1', 
    planId: 'plan-1', 
    equipmentId: 'eq-1', 
    actualDate: yesterdayStr, 
    status: InspectionStatus.OK, 
    inspector: 'Budi Inspector', 
    notes: 'Kondisi APAR sangat baik, area bersih.', 
    checkItems: { 'Tekanan (Zona Hijau)': true, 'Segel & Pin Pengaman': true, 'Kondisi Tabung (No Karat)': true, 'Selang & Nozzle (Tidak Retak)': true, 'Label Instruksi Jelas': true, 'Akses Tidak Terhalang': true },
    inspectionType: InspectionType.ROUTINE,
    area: 'Plant 1 - Production',
    section: 'Assembly Line'
  }
];

export const MOCK_FINDINGS: Finding[] = [];
